/**
 * Lightweight in-memory error tracker used by the global HelpBeacon to
 * detect "something just broke" moments and pre-fill a high-priority
 * support ticket. Captures:
 *   • Unhandled JS errors (window.onerror)
 *   • Unhandled promise rejections (window.onunhandledrejection)
 *   • Failed API responses with status >= 500 OR network errors (via the
 *     axios response interceptor in /lib/api.js)
 *
 * Stored as a rolling buffer of the last 20 events. Consumers call
 * `getRecentErrors(windowMs)` to read events newer than the window.
 *
 * Deliberately client-side only — no remote logging, no PII beyond URL +
 * status code + truncated message.
 */

const BUFFER_SIZE = 20;
const buffer = [];

function push(entry) {
  buffer.push({ ...entry, ts: Date.now() });
  if (buffer.length > BUFFER_SIZE) buffer.shift();
}

export function recordJsError(message, source) {
  if (!message) return;
  push({
    kind: "js_error",
    message: String(message).slice(0, 200),
    source: source ? String(source).slice(0, 120) : undefined,
  });
}

export function recordApiError({ status, url, message }) {
  push({
    kind: "api_error",
    status: status || 0,
    url: url ? String(url).slice(0, 200) : undefined,
    message: message ? String(message).slice(0, 200) : undefined,
  });
}

export function getRecentErrors(windowMs = 30_000) {
  const cutoff = Date.now() - windowMs;
  return buffer.filter((e) => e.ts >= cutoff);
}

let installed = false;
export function installGlobalErrorTracker() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("error", (e) => {
    // Filter noise from ResizeObserver loop and cross-origin script errors
    if (!e?.message || /ResizeObserver/i.test(e.message)) return;
    recordJsError(e.message, e.filename);
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason = e?.reason;
    const msg = reason?.message || (typeof reason === "string" ? reason : JSON.stringify(reason));
    recordJsError(msg, "unhandledrejection");
  });
}
